import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaChevronDown, 
  FaChevronRight, 
  FaMapMarkerAlt, 
  FaStar, 
  FaCalendarAlt, 
  FaPlus,
  FaTimes,
  FaSave
} from 'react-icons/fa';
import { LocationCategory, Location } from '../hooks/useLocationsData';

interface LocationsListProps {
  categories: LocationCategory[];
  locations: Location[];
  onLocationSelect: (location: Location) => void;
  selectedLocationId?: string;
  getLocationsByCategory: (categoryId: string) => Location[];
  onAddLocation?: (locationData: Omit<Location, 'id' | 'createdAt'>) => Promise<void>;
}

export const LocationsList: React.FC<LocationsListProps> = ({
  categories,
  locations,
  onLocationSelect,
  selectedLocationId,
  getLocationsByCategory,
  onAddLocation
}) => {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // Form state para agregar location
  const [locationForm, setLocationForm] = useState({
    name: '',
    description: '',
    categoryId: '',
    address: '',
    lat: 0,
    lng: 0,
    dateVisited: '',
    rating: 0,
    notes: '',
    isVisible: true
  });

  const toggleCategory = (categoryId: string) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  const handleLocationClick = (location: Location) => {
    onLocationSelect(location);
  };

  const handleAddLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!locationForm.name.trim() || !locationForm.categoryId || !onAddLocation) return;
    
    setIsAdding(true);
    try {
      await onAddLocation(locationForm);
      resetForm();
      setShowAddModal(false);
    } catch (error) {
      alert('Error agregando ubicación: ' + (error as Error).message);
    } finally {
      setIsAdding(false);
    }
  };

  const resetForm = () => {
    setLocationForm({
      name: '',
      description: '',
      categoryId: '',
      address: '',
      lat: 0,
      lng: 0,
      dateVisited: '',
      rating: 0,
      notes: '',
      isVisible: true
    });
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <FaStar
        key={i}
        className={`w-3 h-3 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  if (categories.length === 0) {
    return (
      <div className="bg-white/20 backdrop-blur-xl rounded-2xl p-6 h-full flex items-center justify-center">
        <div className="text-center text-gray-400">
          <FaMapMarkerAlt className="mx-auto text-4xl mb-4" />
          <p>No hay categorías configuradas</p>
          <p className="text-sm mt-2">Configura categorías desde el Dashboard</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white/20 backdrop-blur-xl rounded-2xl shadow-2xl h-full overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-secondary">🗺️ Nuestros Lugares</h3>
              <p className="text-sm text-text-light mt-1">
                {locations.filter(loc => loc.isVisible).length} ubicaciones en {categories.length} categorías
              </p>
            </div>
            {onAddLocation && (
              <button
                onClick={() => setShowAddModal(true)}
                className="p-2 bg-primary/20 hover:bg-primary/30 text-secondary rounded-lg transition-colors"
                title="Agregar ubicación"
              >
                <FaPlus className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Lista scrolleable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {categories.map((category) => {
            const categoryLocations = getLocationsByCategory(category.id);
            const isExpanded = expandedCategory === category.id;

            return (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/10 rounded-xl overflow-hidden"
              >
                {/* Header de categoría */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span 
                      className="text-2xl"
                      style={{ color: category.color }}
                    >
                      {category.icon}
                    </span>
                    <div className="text-left">
                      <h4 className="font-bold text-secondary">{category.name}</h4>
                      <p className="text-xs text-text-light">{category.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-primary/20 text-primary px-2 py-1 rounded-full text-xs font-bold">
                      {categoryLocations.length}
                    </span>
                    {isExpanded ? (
                      <FaChevronDown className="text-text-light" />
                    ) : (
                      <FaChevronRight className="text-text-light" />
                    )}
                  </div>
                </button>

                {/* Lista de ubicaciones */}
                <AnimatePresence>
                  {isExpanded && categoryLocations.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-white/20"
                    >
                      <div className="p-2 space-y-2">
                        {categoryLocations.map((location) => (
                          <motion.button
                            key={location.id}
                            onClick={() => handleLocationClick(location)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`w-full p-3 rounded-lg text-left transition-all ${
                              selectedLocationId === location.id
                                ? 'bg-primary/30 border border-primary/50'
                                : 'bg-white/5 hover:bg-white/10'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <FaMapMarkerAlt 
                                className="text-primary mt-1 flex-shrink-0" 
                                style={{ color: category.color }}
                              />
                              <div className="flex-1 min-w-0">
                                <h5 className="font-semibold text-secondary text-sm truncate">
                                  {location.name}
                                </h5>
                                <p className="text-xs text-text-light mt-1 line-clamp-2">
                                  {location.description}
                                </p>
                                
                                {/* Información adicional */}
                                <div className="flex items-center gap-3 mt-2">
                                  {location.rating && (
                                    <div className="flex items-center gap-1">
                                      {renderStars(location.rating)}
                                    </div>
                                  )}
                                  {location.dateVisited && (
                                    <div className="flex items-center gap-1 text-xs text-text-light">
                                      <FaCalendarAlt className="w-3 h-3" />
                                      <span>{formatDate(location.dateVisited)}</span>
                                    </div>
                                  )}
                                </div>

                                {location.address && (
                                  <p className="text-xs text-gray-400 mt-1 truncate">
                                    📍 {location.address}
                                  </p>
                                )}
                              </div>
                            </div>
                          </motion.button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Mensaje si no hay ubicaciones */}
                {isExpanded && categoryLocations.length === 0 && (
                  <div className="p-4 text-center text-gray-400 border-t border-white/20">
                    <p className="text-sm">No hay ubicaciones en esta categoría</p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Footer con estadísticas */}
        <div className="p-4 border-t border-white/20 bg-white/5">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-lg font-bold text-text-light">
                {categories.length}
              </div>
              <div className="text-xs text-text-light">Categorías</div>
            </div>
            <div>
              <div className="text-lg font-bold text-secondary">
                {locations.filter(loc => loc.isVisible).length}
              </div>
              <div className="text-xs text-text-light">Lugares</div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal para agregar ubicación */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white/20 backdrop-blur-md rounded-2xl p-6 max-w-md w-full shadow-2xl border border-white/10"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  📍 Nueva Ubicación
                </h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes className="w-4 h-4" />
                </button>
              </div>

              <form onSubmit={handleAddLocation} className="space-y-4">
                <input
                  type="text"
                  placeholder="Nombre del lugar..."
                  value={locationForm.name}
                  onChange={(e) => setLocationForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white/80"
                  required
                  autoFocus
                />

                <select
                  value={locationForm.categoryId}
                  onChange={(e) => setLocationForm(prev => ({ ...prev, categoryId: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-white/80"
                  required
                >
                  <option value="">Seleccionar categoría</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </option>
                  ))}
                </select>

                <textarea
                  placeholder="Descripción del lugar..."
                  value={locationForm.description}
                  onChange={(e) => setLocationForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white/80 h-20 resize-none"
                />

                <input
                  type="text"
                  placeholder="Dirección (opcional)..."
                  value={locationForm.address}
                  onChange={(e) => setLocationForm(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white/80"
                />

                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="number"
                    step="any"
                    placeholder="Latitud"
                    value={locationForm.lat || ''}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, lat: parseFloat(e.target.value) || 0 }))}
                    className="p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white/80"
                    required
                  />

                  <input
                    type="number"
                    step="any"
                    placeholder="Longitud"
                    value={locationForm.lng || ''}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, lng: parseFloat(e.target.value) || 0 }))}
                    className="p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white/80"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="date"
                    value={locationForm.dateVisited}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, dateVisited: e.target.value }))}
                    className="p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white/80"
                  />

                  <select
                    value={locationForm.rating}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, rating: parseInt(e.target.value) }))}
                    className="p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-white/80"
                  >
                    <option value={0}>Sin calificar</option>
                    {[1, 2, 3, 4, 5].map(rating => (
                      <option key={rating} value={rating}>
                        {'⭐'.repeat(rating)} ({rating}/5)
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      resetForm();
                    }}
                    className="flex-1 py-3 px-4 bg-gray-200/80 text-gray-700 rounded-xl hover:bg-gray-300/80 transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={!locationForm.name.trim() || !locationForm.categoryId || isAdding}
                    className="flex-1 py-3 px-4 bg-gradient-to-r from-primary to-secondary text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 font-medium shadow-lg flex items-center justify-center gap-2"
                  >
                    {isAdding ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <FaSave className="w-4 h-4" />
                    )}
                    {isAdding ? 'Agregando...' : 'Agregar'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};